import {
    EmployeeContributionsState,
    EmployeeContribution,
    EmployeeContributionsListResult,
    EmployeeSearchDropdownItem,
    PriorYearInfoResponse,
    NextYearProcessDateInfoResponse,
    FileUploadRequest
} from "@/employer/types";
import {
    FetchState,
    ListParams,
    PayrollDateResponse
} from "@/shared/types";
import * as payrollService from "@/shared/api/payrollService";
import * as fileContributionService from "@/shared/api/fileContributionService";
import moment from "moment";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useUserStore } from "@/shared/store/user";
import { useNotifierStore } from "@/shared/store/notifier";
import { usePayrollDashboardStore } from "@/employer/store/payrollDashboard";

export const initialState: EmployeeContributionsState = {
    updateFetchState: FetchState.Uninitialized,
    getFetchState: FetchState.Uninitialized,
    payrollDate: null,
    payrollEditCutOffDateTime: null,
    employeeContributions: [],
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
    pages: [],
    selectedEmployeeId: null,
    searchDropdownList: [],
    isBeforePriorYearCutOff: false,
    isInNextYearProcessDateWindow: false
};

export const useEmployerPayrollStore = defineStore("employerPayroll", {
    state: (): EmployeeContributionsState => (_.cloneDeep(initialState)),
    getters: {
        getContributionsByPageNumber: state => (pageNumber: number): EmployeeContribution[] => {

            const pageObject = state.pages[pageNumber];

            if (typeof pageObject === "undefined") {
                return [];
            }
            return state.employeeContributions.slice(pageObject.index, pageObject.index + pageObject.pageSize);
        },
        payrollEditCutOffDate: state => state.payrollEditCutOffDateTime,
        pageCount: state => {
            return state.totalRecords < state.pageSize ? 1 : Math.floor(
                (state.totalRecords + state.pageSize - 1) / state.pageSize);
        },
        userStore () {
            return useUserStore();
        }
    },
    actions: {
        addEmployeeContributions (result: EmployeeContributionsListResult): void {

            // A new record was added by another system user so we need to refresh all the stored pages.
            if (this.totalRecords !== result.totalRecords) {
                this.pages = [];
            }

            const pages = [ ...this.pages ];
            pages[result.pageNumber] = {
                index: this.employeeContributions.length,
                pageSize: result.pageSize
            };
            this.totalRecords = result.totalRecords;
            this.pageNumber = result.pageNumber;
            this.pageSize = result.pageSize;
            this.employeeContributions = this.employeeContributions.concat(result.employeeContributions);
            this.pages = pages;
        },
        setPayrollDates (
            { nextPayrollDate, payrollDateCutOff }: PayrollDateResponse) {
            this.payrollDate = nextPayrollDate;
            this.payrollEditCutOffDateTime = payrollDateCutOff;
        },
        clearPayrollDates () {
            this.payrollDate = null;
            this.payrollEditCutOffDateTime = null;
        },
        setUpdateFetchState (fetchState: FetchState) {
            this.updateFetchState = fetchState;
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        setGetFetchState (fetchState: FetchState) {
            this.getFetchState = fetchState;
        },
        setPayrollDate (payrollDate: string) {
            this.payrollDate = payrollDate;
        },
        setPayrollEditCutOff (payrollDateCutOff: string) {
            this.payrollEditCutOffDateTime = payrollDateCutOff;
        },
        setSelectedEmployeeId (employeeId: string) {
            this.selectedEmployeeId = employeeId;
        },
        setPriorYearInfo (response: PriorYearInfoResponse) {
            this.isBeforePriorYearCutOff = response.isBeforePriorYearCutOff;
        },
        setNextYearInfo (response: NextYearProcessDateInfoResponse) {
            this.isInNextYearProcessDateWindow = response.isInNextYearProcessDateWindow;
        },
        updateEmployeeContribution (employeeContribution: EmployeeContribution) {
            // there is multiple copies of the same user in the array for reasons-unknown
            for (const item of this.employeeContributions) {
                if (item.employeeId === employeeContribution.employeeId) {
                    Object.assign(item, employeeContribution, { modifiedByPayrollRecalculation: false });
                }
            }
        },
        setSearchDropdownList (employees: EmployeeSearchDropdownItem[]) {
            this.searchDropdownList = [ ...employees ];
        },
        getPriorYearInfo () {

            return fileContributionService.getPriorYearInfo(this.userStore.employerId)
                .then(response => {
                    this.setPriorYearInfo(response.data);
                }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        getNextYearInfo () {
            return fileContributionService.getNextYearProcessDateInfo(this.userStore.employerId)
                .then(response => {
                    this.setNextYearInfo(response.data);
                }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        getContributionsList (
            pageNumber: number, pageSize: number, searchTerm = "", clearState = false) {

            return new Promise<void>((resolve, reject) => {

                const pageObject = clearState ? undefined : (this.pages[pageNumber || 1]);
                if (typeof pageObject !== "undefined" || !this.userStore.employerId) {
                    resolve();
                    return;
                } else {
                    const contributionParameters = {
                        pageNumber,
                        pageSize,
                        employerId: this.userStore.employerId,
                        searchTerm: searchTerm || ""
                    };

                    payrollService.getEmployeeContributions(contributionParameters)
                        .then(response => {
                            this.addEmployeeContributions(response.data);
                            this.setPayrollDate(response.data.nextPayrollDate);
                            this.setPayrollEditCutOff(response.data.nextPayrollEditCutOffDateTime);
                            this.setGetFetchState(FetchState.Loaded);
                            resolve();
                        }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
                }
            });
        },
        getPayrollDateAndCutoffDate (employerId: number) {
            // clear data on every call, ensuring that server is called every time
            this.clearPayrollDates();
            return new Promise<void>((resolve, reject) => {
                payrollService.getPayrollDateAndCutoffDate(employerId)
                    .then(response => {
                        this.setPayrollDates(response.data);
                        resolve();
                    }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
            });
        },
        saveEmployeeContribution (
            employeeContribution: EmployeeContribution): Promise<any> {
            if (this.updateFetchState === FetchState.Loading) return Promise.resolve();

            this.setUpdateFetchState(FetchState.Loading);

            const formattedPayrollDate = moment(this.payrollDate).format("YYYY-MM-DD");

            return payrollService.updateEmployeeContribution(formattedPayrollDate, this.userStore.employerId,
                {
                    employeeId: employeeContribution.employeeId,
                    employeeAmount: employeeContribution.employeeAmount,
                    liftAmount: employeeContribution.liftAmount,
                    employerAmount: employeeContribution.employerAmount,
                    canUpdateEmployeeCurrentAndFuture: employeeContribution.isEmployeeCurrentFuturePayrollUpdate
                }).then(response => {
                // Reset the Values as they may have been changed based on changes from limit calculations and employee amount.
                employeeContribution.liftAmount = response.data.liftAmount;
                employeeContribution.liftLimit = response.data.liftLimit;
                employeeContribution.contributionLimit = response.data.contributionLimit;
                if (!response.data.employerAmount) {
                    response.data.employerAmount = employeeContribution.employerAmount;
                }
                employeeContribution.isEmployeeModified = response.data.isEmployeeModified;
                employeeContribution.employerModifiedBy = response.data.employerModifiedBy;
                employeeContribution.employerAmount = response.data.employerAmount ? response.data.employerAmount : 0;
                employeeContribution.hasChange = response.data.isEmployeeModified || !!response.data.employerModifiedBy;
                this.setUpdateFetchState(FetchState.Loaded);
                this.updateEmployeeContribution(employeeContribution);
                const notifierStore = useNotifierStore();
                // set payroll contributions graph to uninitialized, as it needs refreshed data from the api
                const payrollDashboardStore = usePayrollDashboardStore();
                payrollDashboardStore.setPayrollContributionsGraphFetchState(FetchState.Uninitialized);
                if (response.data.message) {
                    notifierStore.setDanger(response.data.message);
                } else {
                    notifierStore.setSuccess(
                        `The contributions for ${employeeContribution.employeeFirstName} ${employeeContribution.employeeLastName} have been updated.`);
                }
                return response.data;
            }).catch(err => {
                this.setUpdateFetchState(FetchState.Loaded);
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        uploadFundingFile (request: FileUploadRequest) {
            const notifierStore = useNotifierStore();
            return fileContributionService.uploadFundingFile(request, this.userStore.employerId)
                .then(resp => {
                    notifierStore.setInfo("Your file is processing, check the file status below.");
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        }
    }
});
